import {
  ACTIVE_CONTEXT_HEADER,
  ApiError,
  DEFAULT_ACTIVE_CONTEXT,
  type ApiClient,
} from '@kiao-voice/api-client'
import {
  createWebApiClient,
  createWebPublicApiClient,
  getWebApiClient,
  getWebPublicApiClient,
} from '../../lib/web-api-client'

import type {
  Plan,
  BillingErrorCode,
  CardholderPayload,
  PurchaseTrackingPayload,
  SubscribePayload,
  SubscribeResponse,
  GuestCheckoutPayload,
  GuestSessionToken,
  GuestCheckoutResponse,
  MagicLoginRedeemResponse,
  PurchaseCreditPackPayload,
  CreditPackResponse,
  PaymentDeliveryStatusResponse,
  CouponProductKind,
  ValidateCouponPayload,
  ValidateCouponResponse,
  SubscriptionPayload,
  SubscriptionResponse,
  SubscriptionInitialStatusResponse,
  SubscriptionUpgradeQuote,
  UpgradeSubscriptionPayload,
  UpgradeSubscriptionResponse,
  SubscriptionUpgradeStatusResponse,
  SubscriptionRenewalStatusResponse,
  BindCardPayload,
  BindCardResponse,
  BalanceResponse,
  WalletResponse,
  WalletTopUpPayload,
  WalletTopUpResponse,
  InvoiceType,
  InvoiceSettings,
  CardSummary,
  ApiKeyGrantableResource,
  ApiKeyListResponse,
  ApiKeyScope,
  ApiKeySummary,
} from '@kiao-voice/api-contract/billing'
import type { ReferralResponse } from '@kiao-voice/api-contract/referral'
import type { VoiceUsageResponse } from '@kiao-voice/api-contract/voice-usage'

export type {
  Plan,
  BillingErrorCode,
  CardholderPayload,
  PurchaseTrackingPayload,
  SubscribePayload,
  SubscribeResponse,
  GuestCheckoutPayload,
  GuestSessionToken,
  GuestCheckoutResponse,
  MagicLoginRedeemResponse,
  PurchaseCreditPackPayload,
  CreditPackResponse,
  PaymentDeliveryStatusResponse,
  CouponProductKind,
  ValidateCouponPayload,
  ValidateCouponResponse,
  SubscriptionPayload,
  SubscriptionResponse,
  SubscriptionInitialStatusResponse,
  SubscriptionUpgradeQuote,
  UpgradeSubscriptionPayload,
  UpgradeSubscriptionResponse,
  SubscriptionUpgradeStatusResponse,
  SubscriptionRenewalStatusResponse,
  BindCardPayload,
  BindCardResponse,
  BalanceResponse,
  WalletResponse,
  WalletTopUpPayload,
  WalletTopUpResponse,
  InvoiceType,
  InvoiceSettings,
  CardSummary,
  ApiKeyGrantableResource,
  ApiKeyListResponse,
  ApiKeyScope,
  ApiKeySummary,
  ReferralResponse,
  VoiceUsageResponse,
}



export class BillingApiError extends Error {
  readonly status: number
  readonly body: unknown
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Billing API error ${status}`)
    this.name = 'BillingApiError'
    this.status = status
    this.body = body
  }
}


/**
 * Per-call options. Bearer token + `X-Active-Context` are injected by the
 * shared web client from storage (#223) — callers no longer thread a token.
 * `fetchFn` is a test seam; `signal` cancels the request.
 */
interface RequestOpts {
  fetchFn?: typeof fetch
  signal?: AbortSignal
}

function authedClient(opts?: RequestOpts): ApiClient {
  return opts?.fetchFn ? createWebApiClient(opts.fetchFn) : getWebApiClient()
}

function publicClient(opts?: RequestOpts): ApiClient {
  return opts?.fetchFn ? createWebPublicApiClient(opts.fetchFn) : getWebPublicApiClient()
}

const requestFailed = (status: number): string => `Request failed (${status})`

/**
 * Map a thrown `ApiError` to `BillingApiError`, preserving the body (which
 * carries the `errorCode`/`error` the UI maps via billing-error-message.ts) and
 * the legacy message shape. Non-ApiError values (network failures) propagate.
 */
function toBillingError(error: unknown, fallback: (status: number) => string): unknown {
  if (error instanceof ApiError) {
    const code = (error.body as { error?: string } | null | undefined)?.error
    return new BillingApiError(error.status, error.body, code ?? fallback(error.status))
  }
  return error
}

async function authedRequest<T>(
  path: string,
  init: RequestInit,
  opts?: RequestOpts,
  fallback: (status: number) => string = requestFailed
): Promise<T> {
  try {
    return await authedClient(opts).request<T>(path, { ...init, signal: opts?.signal })
  } catch (error) {
    throw toBillingError(error, fallback)
  }
}

async function publicRequest<T>(
  path: string,
  init: RequestInit,
  opts?: RequestOpts,
  fallback: (status: number) => string = requestFailed
): Promise<T> {
  try {
    return await publicClient(opts).request<T>(path, { ...init, signal: opts?.signal })
  } catch (error) {
    throw toBillingError(error, fallback)
  }
}

export async function listPlans(opts: RequestOpts = {}): Promise<Plan[]> {
  const body = await publicRequest<{ plans?: Plan[]; teamPlan?: Plan | null }>(
    '/api/v1/plans',
    { method: 'GET', cache: 'no-store' },
    opts,
    (status) => `Failed to load plans (${status})`
  )
  const personalPlans = body.plans ?? []
  return body.teamPlan
    ? [...personalPlans.filter((plan) => plan.code !== 'team'), body.teamPlan]
    : personalPlans
}

// =============================================================================
// TapPay subscribe / credit-pack — payment is synchronous; the response shape
// tells the frontend whether to render a success state, a decline message, or
// a 3DS redirect.
// =============================================================================





export function subscribeViaTappay(
  payload: SubscribePayload,
  opts: RequestOpts = {}
): Promise<SubscribeResponse> {
  return authedRequest<SubscribeResponse>(
    '/api/v1/billing/subscribe',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}

// =============================================================================
// Token-less (guest) checkout from /pricing — "pay first, verify after". No
// Authorization header: the account is found-or-created by email server-side.
// =============================================================================




export function guestCheckoutViaTappay(
  payload: GuestCheckoutPayload,
  opts: RequestOpts = {}
): Promise<GuestCheckoutResponse> {
  return publicRequest<GuestCheckoutResponse>(
    '/api/v1/billing/guest-checkout',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}


export function redeemMagicLogin(
  token: string,
  opts: RequestOpts = {}
): Promise<MagicLoginRedeemResponse> {
  return publicRequest<MagicLoginRedeemResponse>(
    '/api/v1/auth/magic-login/redeem',
    { method: 'POST', body: JSON.stringify({ token }) },
    opts
  )
}



export function purchaseCreditPackViaTappay(
  payload: PurchaseCreditPackPayload,
  opts: RequestOpts = {}
): Promise<CreditPackResponse> {
  return authedRequest<CreditPackResponse>(
    '/api/v1/billing/credit-pack',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}


export function fetchCreditPackPaymentStatus(
  tradeId: number,
  opts: RequestOpts = {}
): Promise<PaymentDeliveryStatusResponse> {
  return authedRequest<PaymentDeliveryStatusResponse>(
    `/api/v1/billing/credit-pack/payments/${tradeId}`,
    { method: 'GET' },
    opts
  )
}

// =============================================================================
// Coupon (折抵券) preview — soft check for the checkout-modal breakdown. The
// charge sites re-validate authoritatively under lock at submit.
// =============================================================================



export function validateCoupon(
  payload: ValidateCouponPayload,
  opts: RequestOpts = {}
): Promise<ValidateCouponResponse> {
  // Public endpoint, but the bearer rides along when the visitor is signed in
  // so a bound code (`not_yours`) and a spent per-user limit (`already_used`)
  // are refused at preview. Signed out, the shared client sends no token and
  // the request is exactly the anonymous one. The endpoint never answers 401
  // (a dead bearer degrades to anonymous), so the client's 401 self-heal is
  // never reached from here.
  return authedRequest<ValidateCouponResponse>(
    '/api/v1/billing/coupons/validate',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}

// =============================================================================
// Subscription + balance reads, cancel
// =============================================================================



export function fetchSubscription(opts: RequestOpts = {}): Promise<SubscriptionResponse> {
  return authedRequest<SubscriptionResponse>(
    '/api/v1/billing/subscription',
    { method: 'GET' },
    opts
  )
}

/** Read the signed-in user's personal subscription even when another Team is
 * the currently selected workspace. Pricing decisions must never inherit a
 * stale Team `X-Active-Context`, otherwise a Team owner can be mistaken for an
 * existing Team subscriber and blocked from buying or upgrading a personal
 * plan. */
export function fetchPersonalSubscription(opts: RequestOpts = {}): Promise<SubscriptionResponse> {
  return authedRequest<SubscriptionResponse>(
    '/api/v1/billing/subscription',
    { method: 'GET', headers: { [ACTIVE_CONTEXT_HEADER]: DEFAULT_ACTIVE_CONTEXT } },
    opts
  )
}


export function fetchSubscriptionInitialStatus(
  tradeId: number,
  opts: RequestOpts = {}
): Promise<SubscriptionInitialStatusResponse> {
  return authedRequest<SubscriptionInitialStatusResponse>(
    `/api/v1/billing/subscription/payments/${tradeId}`,
    { method: 'GET' },
    opts
  )
}


export function fetchSubscriptionUpgradeQuote(
  opts: RequestOpts = {}
): Promise<{ quote: SubscriptionUpgradeQuote }> {
  return authedRequest<{ quote: SubscriptionUpgradeQuote }>(
    '/api/v1/billing/subscription/upgrade-quote?planCode=pro',
    { method: 'GET' },
    opts
  )
}



export function upgradeSubscription(
  payload: UpgradeSubscriptionPayload,
  opts: RequestOpts = {}
): Promise<UpgradeSubscriptionResponse> {
  return authedRequest<UpgradeSubscriptionResponse>(
    '/api/v1/billing/subscription/upgrade',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}


export function fetchSubscriptionUpgradeStatus(
  tradeId: number,
  opts: RequestOpts = {}
): Promise<SubscriptionUpgradeStatusResponse> {
  return authedRequest<SubscriptionUpgradeStatusResponse>(
    `/api/v1/billing/subscription/upgrades/${tradeId}`,
    { method: 'GET' },
    opts
  )
}


export function fetchSubscriptionRenewalStatus(
  tradeId: number,
  opts: RequestOpts = {}
): Promise<SubscriptionRenewalStatusResponse> {
  return authedRequest<SubscriptionRenewalStatusResponse>(
    `/api/v1/billing/subscription/renewals/${tradeId}`,
    { method: 'GET' },
    opts
  )
}

export function cancelSubscription(opts: RequestOpts = {}): Promise<SubscriptionResponse> {
  return authedRequest<SubscriptionResponse>(
    '/api/v1/billing/subscription/cancel',
    { method: 'POST', body: '{}' },
    opts
  )
}

export function reactivateSubscription(opts: RequestOpts = {}): Promise<SubscriptionResponse> {
  return authedRequest<SubscriptionResponse>(
    '/api/v1/billing/subscription/reactivate',
    { method: 'POST', body: '{}' },
    opts
  )
}



export function bindCard(
  payload: BindCardPayload,
  opts: RequestOpts = {}
): Promise<BindCardResponse> {
  return authedRequest<BindCardResponse>(
    '/api/v1/billing/cards/bind',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}


export function fetchBalance(opts: RequestOpts = {}): Promise<BalanceResponse> {
  return authedRequest<BalanceResponse>('/api/v1/credits/balance', { method: 'GET' }, opts)
}

// =============================================================================
// Money wallet (儲值金) — prepay-able org balance, deducted first at checkout.
// Flag-gated server-side (app_settings wallet.enabled); `enabled:false` tells
// the web to hide the top-up UI. Wholly separate from the batch credit ledger.
// =============================================================================


export function fetchWallet(opts: RequestOpts = {}): Promise<WalletResponse> {
  return authedRequest<WalletResponse>('/api/v1/billing/wallet', { method: 'GET' }, opts)
}

/** Pricing personal plans always spend the account's personal wallet, even if
 * the current workspace is a Team. Keep the amount preview identical to the
 * server-side subscription charge authority. */
export function fetchPersonalWallet(opts: RequestOpts = {}): Promise<WalletResponse> {
  return authedRequest<WalletResponse>(
    '/api/v1/billing/wallet',
    { method: 'GET', headers: { [ACTIVE_CONTEXT_HEADER]: DEFAULT_ACTIVE_CONTEXT } },
    opts
  )
}

// =============================================================================
// Referral program (issue #71) — the user's shareable code + stats. Flag-gated
// server-side (app_settings referral.enabled); `enabled:false` tells the web to
// hide the referral panel. The grant itself fires server-side at the referee's
// first paid charge — there is no client-driven grant call.
// =============================================================================


export function fetchReferral(opts: RequestOpts = {}): Promise<ReferralResponse> {
  return authedRequest<ReferralResponse>('/api/v1/referral', { method: 'GET' }, opts)
}



export function topUpWalletViaTappay(
  payload: WalletTopUpPayload,
  opts: RequestOpts = {}
): Promise<WalletTopUpResponse> {
  return authedRequest<WalletTopUpResponse>(
    '/api/v1/billing/wallet/top-up',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}

export function fetchWalletTopUpPaymentStatus(
  tradeId: number,
  opts: RequestOpts = {}
): Promise<PaymentDeliveryStatusResponse> {
  return authedRequest<PaymentDeliveryStatusResponse>(
    `/api/v1/billing/wallet/top-ups/${tradeId}`,
    { method: 'GET' },
    opts
  )
}

// =============================================================================
// Voice usage — the desktop voice-input meter, surfaced read-only on the
// billing page. Wholly separate from the batch credit ledger above.
// =============================================================================


export function fetchVoiceUsage(opts: RequestOpts = {}): Promise<VoiceUsageResponse> {
  return authedRequest<VoiceUsageResponse>('/api/v1/voice-usage/me', { method: 'GET' }, opts)
}

// =============================================================================
// E-invoice settings — org-scoped (per active context). Personal mode issues to
// the account email; company mode carries the 統一編號 / 抬頭 / 地址 identity.
// Settings storage only — issuance is a later slice of the e-invoice epic.
// =============================================================================


export function fetchInvoiceSettings(
  opts: RequestOpts = {}
): Promise<{ invoiceSettings: InvoiceSettings }> {
  return authedRequest<{ invoiceSettings: InvoiceSettings }>(
    '/api/v1/billing/invoice-settings',
    { method: 'GET' },
    opts
  )
}

/**
 * Team checkout always belongs to the buyer's personal account until the new
 * Team organization exists. Explicitly override a currently selected Team
 * context so another organization's legal invoice identity cannot be copied
 * into the buyer's new Team.
 */
export function fetchPersonalInvoiceSettings(
  opts: RequestOpts = {}
): Promise<{ invoiceSettings: InvoiceSettings }> {
  return authedRequest<{ invoiceSettings: InvoiceSettings }>(
    '/api/v1/billing/invoice-settings',
    { method: 'GET', headers: { [ACTIVE_CONTEXT_HEADER]: DEFAULT_ACTIVE_CONTEXT } },
    opts
  )
}

export function saveInvoiceSettings(
  payload: InvoiceSettings,
  opts: RequestOpts = {}
): Promise<{ invoiceSettings: InvoiceSettings }> {
  return authedRequest<{ invoiceSettings: InvoiceSettings }>(
    '/api/v1/billing/invoice-settings',
    { method: 'PUT', body: JSON.stringify(payload) },
    opts
  )
}

/** Personal subscriptions always issue against the account's personal billing
 * organization, regardless of the workspace currently selected in the app. */
export function savePersonalInvoiceSettings(
  payload: InvoiceSettings,
  opts: RequestOpts = {}
): Promise<{ invoiceSettings: InvoiceSettings }> {
  return authedRequest<{ invoiceSettings: InvoiceSettings }>(
    '/api/v1/billing/invoice-settings',
    {
      method: 'PUT',
      headers: { [ACTIVE_CONTEXT_HEADER]: DEFAULT_ACTIVE_CONTEXT },
      body: JSON.stringify(payload),
    },
    opts
  )
}

// =============================================================================
// Cards
// =============================================================================


export function fetchCards(opts: RequestOpts = {}): Promise<{ cards: CardSummary[] }> {
  return authedRequest<{ cards: CardSummary[] }>('/api/v1/billing/cards', { method: 'GET' }, opts)
}

export function removeCard(id: number, opts: RequestOpts = {}): Promise<void> {
  return authedRequest<void>(`/api/v1/billing/cards/${id}`, { method: 'DELETE' }, opts)
}

export function setDefaultCard(
  id: number,
  opts: RequestOpts = {}
): Promise<{ card: { id: number; defaultCard: boolean } }> {
  return authedRequest<{ card: { id: number; defaultCard: boolean } }>(
    `/api/v1/billing/cards/${id}/default`,
    { method: 'POST', body: '{}' },
    opts
  )
}

// =============================================================================
// API keys (unchanged from the Stripe era, kept here for now)
// =============================================================================



export function fetchApiKeys(opts: RequestOpts = {}): Promise<ApiKeyListResponse> {
  return authedRequest<ApiKeyListResponse>('/api/v1/api-keys', { method: 'GET' }, opts)
}

export function createApiKey(
  payload: { name: string; scopes: ApiKeyScope[]; expiresAt?: string },
  opts: RequestOpts = {}
): Promise<{ apiKey: ApiKeySummary; rawKey: string }> {
  return authedRequest<{ apiKey: ApiKeySummary; rawKey: string }>(
    '/api/v1/api-keys',
    { method: 'POST', body: JSON.stringify(payload) },
    opts
  )
}

export function updateApiKey(
  id: number,
  payload: { name?: string; scopes?: ApiKeyScope[] },
  opts: RequestOpts = {}
): Promise<{ apiKey: ApiKeySummary }> {
  return authedRequest<{ apiKey: ApiKeySummary }>(
    `/api/v1/api-keys/${id}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    opts
  )
}

export function deleteApiKey(id: number, opts: RequestOpts = {}): Promise<void> {
  return authedRequest<void>(`/api/v1/api-keys/${id}`, { method: 'DELETE' }, opts)
}
